import { useState } from "react";
import { Send, CheckCircle2, Sparkles, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { programs } from "../mock/mock";
import { submitAdmission } from "../services/api";

const AdmissionPage = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    parentName: "",
    childName: "",
    childAge: "",
    email: "",
    phone: "",
    program: "",
    message: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitAdmission(form);
      setSubmitted(true);
      toast({ title: "Application Received!", description: "We will contact you within 24 hours." });
      setTimeout(() => {
        setSubmitted(false);
        setForm({ parentName: "", childName: "", childAge: "", email: "", phone: "", program: "", message: "" });
      }, 3000);
    } catch (err) {
      toast({ title: "Submission failed", description: err.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div>
      <section className="pt-16 pb-16 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm border border-orange-100 mb-4">
            <Sparkles className="w-4 h-4 text-orange-600" />
            <span className="text-xs font-semibold text-orange-700 tracking-widest uppercase">Admissions 2025-26</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-stone-800 mb-4">
            Begin Your Child's <span className="text-orange-600">Journey</span>
          </h1>
          <p className="text-stone-600 max-w-2xl mx-auto">Fill this simple form and our admissions team will reach out to you within 24 hours.</p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-orange-50 to-amber-50 rounded-3xl p-8 md:p-10 border border-orange-100 shadow-xl">
            {submitted ? (
              <div className="text-center py-10">
                <div className="w-20 h-20 rounded-full bg-green-100 mx-auto mb-4 flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="font-display text-2xl font-bold text-stone-800 mb-2">Thank You!</h3>
                <p className="text-stone-600">Your enquiry has been received. Our team will contact you shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="parentName" className="text-stone-700 font-semibold">Parent's Name *</Label>
                    <Input id="parentName" required value={form.parentName} onChange={(e) => set("parentName", e.target.value)} placeholder="Full Name" className="mt-1.5 bg-white h-11 border-orange-200 focus-visible:ring-orange-500" />
                  </div>
                  <div>
                    <Label htmlFor="childName" className="text-stone-700 font-semibold">Child's Name *</Label>
                    <Input id="childName" required value={form.childName} onChange={(e) => set("childName", e.target.value)} placeholder="Child's Name" className="mt-1.5 bg-white h-11 border-orange-200 focus-visible:ring-orange-500" />
                  </div>
                  <div>
                    <Label htmlFor="childAge" className="text-stone-700 font-semibold">Child's Age *</Label>
                    <Input id="childAge" required value={form.childAge} onChange={(e) => set("childAge", e.target.value)} placeholder="e.g. 3 years" className="mt-1.5 bg-white h-11 border-orange-200 focus-visible:ring-orange-500" />
                  </div>
                  <div>
                    <Label className="text-stone-700 font-semibold">Program of Interest *</Label>
                    <Select value={form.program} onValueChange={(v) => set("program", v)} required>
                      <SelectTrigger className="mt-1.5 bg-white h-11 border-orange-200">
                        <SelectValue placeholder="Select a program" />
                      </SelectTrigger>
                      <SelectContent>
                        {programs.map((p) => (
                          <SelectItem key={p.id} value={p.name}>{p.name} • {p.ageGroup}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-stone-700 font-semibold">Email *</Label>
                    <Input id="email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="your@email.com" className="mt-1.5 bg-white h-11 border-orange-200 focus-visible:ring-orange-500" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-stone-700 font-semibold">Phone *</Label>
                    <Input id="phone" required value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 XXXXX XXXXX" className="mt-1.5 bg-white h-11 border-orange-200 focus-visible:ring-orange-500" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="message" className="text-stone-700 font-semibold">Message (Optional)</Label>
                  <Textarea id="message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Any questions or special requirements?" className="mt-1.5 bg-white border-orange-200 focus-visible:ring-orange-500 min-h-[100px]" />
                </div>
                <Button type="submit" size="lg" disabled={loading} className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-full h-12 shadow-lg btn-shine">
                  {loading ? (
                    <><Loader2 className="mr-2 w-4 h-4 animate-spin" /> Submitting...</>
                  ) : (
                    <>Submit Enquiry <Send className="ml-2 w-4 h-4" /></>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdmissionPage;
